/**
 * Helpers for the paid-tariff subscription returned by
 * GET /api/payments/subscription (PaymentService.getSubscription):
 *   { id, tariff_name, price_uah, period, status, started_at, expires_at,
 *     grace_until, in_grace }
 * The endpoint answers 204 (empty body) when there is no active subscription,
 * and Free has `expires_at = null` (= open-ended).
 *
 * Grace period (backend `SubscriptionGrace.GRACE_DAYS = 3`): after `expires_at`
 * the paid tariff keeps working until `grace_until` with `in_grace = true` and
 * `status = "ACTIVE"`. After that the company is downgraded to Free and every
 * master except the owner is deactivated (`deactivatedByDowngrade`).
 *
 * `expires_at` is a server LocalDateTime without timezone ("2026-10-12T14:30:00"),
 * so it is parsed field-by-field into a local calendar date instead of going
 * through `new Date(isoString)`, and "days left" is a difference of calendar
 * dates (UTC-based to be immune to DST shifts), not floor(ms / 86400000).
 */

export const EXPIRY_WARNING_DAYS = 7;

/** "2026-10-12T14:30:00" → local Date at midnight of 2026-10-12, or null. */
export function parseLocalDate(value) {
    if (!value || typeof value !== 'string') return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (!m) return null;
    const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(date.getTime()) ? null : date;
}

/** Calendar days from today (local) until `date`; 0 = today, negative = past. */
export function calendarDaysUntil(date, now = new Date()) {
    if (!date) return null;
    const target = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
    const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
    return Math.round((target - today) / 86400000);
}

/** "12 жовтня 2026" */
export function formatExpiryDate(date) {
    if (!date) return '';
    return date
        .toLocaleDateString('uk-UA', { day: 'numeric', month: 'long', year: 'numeric' })
        .replace(/\s*р\.?$/, '');
}

/**
 * Normalizes the raw subscription response for a paid tariff with an expiry
 * date into
 *   { tariffName, expiresDate, graceDate, inGrace, daysLeft, expiringSoon }
 * or null for Free / open-ended / missing / error responses.
 *
 * `daysLeft` counts down to `expires_at` normally and to `grace_until` while in
 * grace; it is clamped at 0 (never negative). `expiringSoon` is always true in
 * grace.
 */
export function getPaidExpiry(subscription, now = new Date()) {
    if (!subscription || typeof subscription !== 'object' || subscription.error) return null;
    const price = Number(subscription.price_uah);
    if (!(price > 0)) return null;
    const expiresDate = parseLocalDate(subscription.expires_at);
    if (!expiresDate) return null;
    const graceDate = parseLocalDate(subscription.grace_until);
    const inGrace = subscription.in_grace === true;
    const target = inGrace && graceDate ? graceDate : expiresDate;
    const daysLeft = Math.max(0, calendarDaysUntil(target, now));
    return {
        tariffName: subscription.tariff_name,
        expiresDate,
        graceDate,
        inGrace,
        daysLeft,
        expiringSoon: inGrace || daysLeft <= EXPIRY_WARNING_DAYS,
    };
}

/** "сьогодні" / "завтра" / "через 5 дн." */
export function formatDaysLeft(daysLeft) {
    if (daysLeft <= 0) return 'сьогодні';
    if (daysLeft === 1) return 'завтра';
    return `через ${daysLeft} дн.`;
}

/** "Залишилось 5 днів" style label for the tariffs page. */
export function formatDaysLeftLong(daysLeft) {
    if (daysLeft <= 0) return 'Закінчується сьогодні';
    if (daysLeft === 1) return 'Закінчується завтра';
    const mod10 = daysLeft % 10;
    const mod100 = daysLeft % 100;
    let word = 'днів';
    if (mod10 === 1 && mod100 !== 11) word = 'день';
    else if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) word = 'дні';
    const verb = mod10 === 1 && mod100 !== 11 ? 'Залишився' : 'Залишилось';
    return `${verb} ${daysLeft} ${word}`;
}
